import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Check, FileSearch, Info, Loader2 } from "lucide-react";
import { apiClient } from "@/api/client";
import { formatApiError } from "@/utils/httpErrors";
import { formatCurrency, DEFAULT_CURRENCY } from "@/lib/currency";
import { ScaleNotice, type HeldScale, type ResolvedScale } from "./ScaleNotice";

/**
 * DOC-INTAKE-1 Job 2 — run extraction over a deal's uploaded documents and
 * pre-fill the P&L grid.
 *
 * Every pre-filled figure is a DRAFT that the CPA must review. Nothing here
 * approves anything on their behalf: the panel's job is to make visible what
 * the machine did, what it refused to touch, and what it could not read — so
 * "AI does the grunt work, the CPA stays accountable" is observable, not a
 * claim. Skipped cells and unreadable files are shown as prominently as
 * successes; a silent gap is the failure mode that matters here.
 */

export interface ProvenanceCell {
  field: string;
  value: number;
  filename: string;
  parser: string;
  source_label: string;
  locator: string;
  confidence: number;
  status: "draft" | "accepted";
  sign_normalized?: boolean;
  year_verified?: boolean;
  note?: string;
  accepted_by?: string;
}

export type ProvenanceMap = Record<string, ProvenanceCell>;

interface RunReport {
  filled: { year: number; field: string; value: number }[];
  updated: { year: number; field: string; value: number }[];
  skipped_cpa_edited: {
    year: number; field: string; kept: number; extracted: number; reason: string;
  }[];
  unassigned_period: { filename: string; period_label: string; fields: Record<string, number> }[];
  supporting: { filename: string; type: string }[];
  /**
   * EXTRACT-MAP-1: source lines the reader saw but did not carry into the grid,
   * each with the reason. Older runs were saved before this key existed, so it
   * is optional — an absent list means "this run predates the record", which is
   * not the same as "nothing was left out".
   */
  unmapped?: {
    filename: string; source_label: string; value: number | null;
    locator: string; reason: string;
  }[];
  /**
   * EXTRACT-UX-1: the backend now sends `{filename, warning}`, but runs saved
   * before that stored a bare string, and `_last_run` lives on disk. Both
   * shapes are declared because both still arrive — rendering the object shape
   * against a string is exactly what produced a "thing to check" with nothing
   * in it but a colon.
   */
  warnings: (string | { filename?: string; warning?: string })[];
  errors: (string | { filename?: string; error?: string })[];
  /**
   * EXTRACT-PERIOD-2: periods whose fiscal year did not survive the
   * plausibility check and are waiting on the CPA. Optional because runs saved
   * before this existed have no such key.
   */
  held_period?: PeriodEntry[];
  /** Periods the CPA discarded — kept visible, never silently forgotten. */
  discarded_period?: PeriodEntry[];
  period_decisions?: Record<string, PeriodDecision>;
  /**
   * EXTRACT-UNITS-1: documents whose printed unit ("in thousands") is not yet
   * confirmed, and so are being held out of the grid entirely. Optional for the
   * same reason as `held_period` — runs saved before this existed have no key,
   * and an absent list means "not asked", not "whole dollars".
   */
  held_scale?: HeldScale[];
  resolved_scale?: ResolvedScale[];
  scale_decisions?: Record<string, PeriodDecision>;
  run_at?: string;
  run_by?: string;
}

/**
 * A period the reader dated to a year that does not look real — a future year,
 * a year far outside the rest of the set. Held periods are NOT in the grid;
 * discarded ones have been taken back out of it.
 */
interface PeriodEntry {
  year: number;
  filename: string;
  period_label: string;
  basis?: string;
  reason: string;
  fields: Record<string, number>;
  /** Figures a human had entered or approved, which a discard leaves alone. */
  kept_fields?: Record<string, number>;
  in_grid?: boolean;
}

interface PeriodDecision {
  decision: string;
  by?: string;
  at?: string;
}

interface Props {
  dealId: string;
  /** Years the grid can actually display — figures outside are flagged. */
  visibleYears: readonly number[];
  provenance: ProvenanceMap;
  lastRun: RunReport | null;
  /** CURRENCY-1: the deal's currency, so this panel reads like the grid. */
  currency?: string;
  /**
   * EXTRACT-REFRESH-1: true while the page-level reload that `onExtracted`
   * kicks off is still in flight (normalization + provenance + custom-items
   * GETs). A POST returning 200 is not the same as the grid being caught up —
   * this panel used to treat "the POST resolved" as "idle", so if that reload
   * hung, the button looked ready again with nothing visibly wrong, and a CPA
   * would re-click it and trigger a second real extraction on top of the
   * first. See `run()` below for how this is turned into a visible state and
   * an honest timeout instead.
   */
  refreshing: boolean;
  /** Called after a successful run so the page can reload the grid. */
  onExtracted: () => void;
}

const FIELD_LABEL: Record<string, string> = {
  revenue: "Revenue",
  cogs: "COGS",
  operating_expenses: "Operating expenses",
  depreciation: "Depreciation",
  amortization: "Amortization",
  interest: "Interest",
  tax: "Tax",
};

export const fieldLabel = (f: string): string => FIELD_LABEL[f] ?? f;

// CURRENCY-1: the deal's currency, not a hardcoded dollar sign, and cents kept
// so a figure on screen ties back to the trial balance.
const fmt = (n: number | null | undefined, currency: string = DEFAULT_CURRENCY): string =>
  formatCurrency(n, currency);

// EXTRACT-UX-1: what to say when a check arrives with no text at all. It is
// still a sentence, and it still names the next action — an empty line on this
// panel reads as "nothing is wrong", which is the one thing it does not mean.
const UNDESCRIBED_CHECK =
  "Something was flagged on this run but the reason was not recorded — run " +
  "extraction again, and check the figures against the source documents.";

// EXTRACT-REFRESH-1: how long to hold the button in "Refreshing…" before
// telling the CPA honestly that the reload did not confirm — rather than
// holding it forever (a stuck spinner reads as a frozen page) or clearing it
// silently (the exact ambiguity that let a CPA re-click into a second real
// run). 20s comfortably clears a normal three-GET reload with margin.
const REFRESH_TIMEOUT_MS = 20_000;

/**
 * One "thing to check", rendered as a finished plain-language sentence.
 *
 * The panel used to render `{w.filename}: {w.warning}` over values that were
 * often plain strings, so both halves came out `undefined` and the CPA was
 * shown a bare colon. Every shape now resolves to something readable, and the
 * filename is only prefixed when the message does not already start with it —
 * the reader embeds it in most warnings itself.
 */
const checkSentence = (
  item: string | { filename?: string; warning?: string; error?: string } | null | undefined,
): string => {
  if (typeof item === "string") return item.trim() || UNDESCRIBED_CHECK;
  const text = String(item?.warning ?? item?.error ?? "").trim();
  const file = String(item?.filename ?? "").trim();
  if (!text) {
    return file
      ? `${file} was flagged but the reason was not recorded — run extraction ` +
        `again, and check its figures against the statement.`
      : UNDESCRIBED_CHECK;
  }
  if (!file || text.toLowerCase().startsWith(file.toLowerCase())) return text;
  return `${file}: ${text}`;
};

/** The figures a held period is carrying, named rather than merely counted. */
const summarizeFields = (
  fields: Record<string, number> | undefined, currency: string,
): string => {
  const parts = Object.entries(fields || {}).map(
    ([f, v]) => `${fieldLabel(f)} ${fmt(v, currency)}`,
  );
  if (parts.length === 0) return "no figures";
  if (parts.length <= 2) return parts.join(" and ");
  return `${parts.slice(0, 2).join(", ")} and ${parts.length - 2} more`;
};

export const ExtractionPanel: React.FC<Props> = ({
  dealId, visibleYears, provenance, lastRun, currency = DEFAULT_CURRENCY, refreshing, onExtracted,
}) => {
  const [running, setRunning] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // EXTRACT-EMPTY-1: a successful run that had nothing to read. Kept apart from
  // `error` so it renders muted rather than as a red failure.
  const [notice, setNotice] = useState<string | null>(null);
  const [showMap, setShowMap] = useState(false);

  // EXTRACT-REFRESH-1: true from the moment the POST resolves until the
  // page-level reload it kicked off (`refreshing`, owned by the parent) is
  // confirmed caught up — or until `REFRESH_TIMEOUT_MS` passes without that
  // confirmation, at which point `refreshTimedOut` takes over instead of
  // leaving this on forever.
  const [awaitingRefresh, setAwaitingRefresh] = useState(false);
  const [refreshTimedOut, setRefreshTimedOut] = useState(false);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRefreshTimer = () => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  };

  // The parent's reload finished — drop the "Refreshing…" state. Runs on
  // every `refreshing` transition, but only has anything to do while this
  // panel is actually the one waiting on it.
  useEffect(() => {
    if (!refreshing && awaitingRefresh) {
      clearRefreshTimer();
      setAwaitingRefresh(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshing]);

  // Unmount guard — a deal switch away from this panel mid-reload should not
  // leave a timer firing into state nobody is reading.
  useEffect(() => () => clearRefreshTimer(), []);

  const cells = Object.entries(provenance);
  const pending = cells.filter(([, c]) => c.status !== "accepted");
  const accepted = cells.length - pending.length;

  // EXTRACT-REFRESH-1: `awaitingRefresh` holds the guard through the reload
  // window, not just the POST — a re-click here used to be free precisely
  // because `running` had already gone false while the reload was still
  // outstanding.
  const busy = running || accepting || awaitingRefresh;

  // EXTRACT-PERIOD-2: periods the reader dated to a year that does not look
  // real. They are not in the grid and will not enter it until a CPA says so.
  const held = lastRun?.held_period ?? [];
  const discarded = lastRun?.discarded_period ?? [];

  // EXTRACT-UNITS-1: documents whose printed unit is unconfirmed. These are held
  // whole — not one period of them is in the grid — because the unit belongs to
  // the statement, so a partial load would mix dollars and thousands.
  const heldScale = lastRun?.held_scale ?? [];
  const resolvedScale = lastRun?.resolved_scale ?? [];

  // A year with provenance but no column on screen. With periods derived from
  // the deal's own data this should only be a moment between the run finishing
  // and the grid reloading — the figures are saved either way, and the copy
  // below says exactly that rather than claiming they are being thrown away.
  const outOfWindow = Array.from(
    new Set(
      cells
        .map(([key]) => Number(key.split(".")[0]))
        .filter(y => Number.isFinite(y) && !visibleYears.includes(y))
    )
  ).sort();

  // EXTRACT-MAP-1: one row per source line the reader took a figure from —
  // what the document called it, which grid line it became, and for which year.
  // Without this the CPA has to trust a number whose origin is invisible.
  const mappedRows = useMemo(
    () =>
      Object.entries(provenance)
        .map(([key, c]) => ({ key, year: Number(key.split(".")[0]), cell: c }))
        .sort(
          (a, b) =>
            (a.year || 0) - (b.year || 0) || a.cell.field.localeCompare(b.cell.field),
        ),
    [provenance],
  );

  // Lines the reader saw and deliberately left out. An absent list means the
  // run predates this record — not that nothing was left out — so the empty
  // state below says which of the two it is.
  const unmappedRows = lastRun?.unmapped;
  const mapCount = mappedRows.length + (unmappedRows?.length ?? 0);

  /**
   * Run the pass. `decision` answers a held period — the same endpoint, because
   * extraction here is deterministic and costs nothing to repeat, so a confirm
   * or a discard is simply the run done again with the CPA's verdict attached.
   */
  const run = async (decision?: {
    confirm_years?: number[]; discard_years?: number[];
    confirm_scale?: string[]; decline_scale?: string[];
  }) => {
    setRunning(true);
    setError(null);
    setNotice(null);
    setRefreshTimedOut(false);
    try {
      // EXTRACT-EMPTY-1: a deal with nothing uploaded answers 200 with a
      // `message` and no documents read. Discarding the body made that case
      // indistinguishable from a no-op — the CPA clicked "Run extraction" and
      // the panel simply refreshed to the same empty state. It is a normal
      // state, not an error, so it shows as a calm notice rather than red.
      const res = await apiClient.post<{ documents_read?: number; message?: string }>(
        `/qoe/deals/${dealId}/extract`, decision ?? {},
      );
      if (res.data?.message) setNotice(res.data.message);
      // EXTRACT-REFRESH-1: the POST succeeding is not the same as the grid
      // being caught up. Kick off the reload and hold the guard through it —
      // cleared by the `refreshing` effect above on the normal path, or by
      // this timer on a hung one. Without this, `finally` below cleared
      // `running` the instant the POST returned, and the button looked ready
      // for a re-click while the real reload was still outstanding.
      setAwaitingRefresh(true);
      clearRefreshTimer();
      refreshTimer.current = setTimeout(() => {
        refreshTimer.current = null;
        setAwaitingRefresh(false);
        setRefreshTimedOut(true);
      }, REFRESH_TIMEOUT_MS);
      onExtracted();
    } catch (e: unknown) {
      setError(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  const acceptAll = async () => {
    setAccepting(true);
    setError(null);
    setNotice(null);
    try {
      await apiClient.post(`/qoe/deals/${dealId}/extraction/accept`, {
        keys: pending.map(([k]) => k),
      });
      onExtracted();
    } catch (e: unknown) {
      setError(formatApiError(e));
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-brand-text flex items-center gap-1.5">
            <FileSearch className="h-4 w-4 shrink-0" />
            Extract from documents
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Reads the seller's uploads and pre-fills the grid as draft figures.
            Every value stays yours to check — nothing is approved until you say so.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {pending.length > 0 && (
            <Button
              size="sm" variant="outline" onClick={acceptAll} disabled={busy}
            >
              {accepting
                ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                : <Check className="h-3.5 w-3.5 mr-1" />}
              Approve {pending.length} draft{pending.length === 1 ? "" : "s"}
            </Button>
          )}
          {/* EXTRACT-REFRESH-1: `busy` (not just `running`) drives both the
              disabled state and the label, so the button stays honest about
              the reload window too, not just the POST. */}
          <Button size="sm" onClick={() => run()} disabled={busy}>
            {(running || awaitingRefresh) && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
            {running ? "Reading…" : awaitingRefresh ? "Refreshing…" : "Run extraction"}
          </Button>
        </div>
      </div>

      {error && (
        <p className="mt-2 text-xs text-destructive flex items-start gap-1">
          <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />
          <span>{error}</span>
        </p>
      )}

      {/* EXTRACT-REFRESH-1: the extraction itself succeeded — this is not
          `error` (nothing needs re-running) and not `notice` (it is not a
          calm all-clear either). A CPA who sees this should reload the page
          before running extraction again, rather than trusting a grid that
          may not have caught up. */}
      {refreshTimedOut && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-400 flex items-start gap-1">
          <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />
          <span>
            The extraction was saved, but the page hasn't confirmed the grid
            refreshed after {Math.round(REFRESH_TIMEOUT_MS / 1000)}s. Reload
            the page to see the results before running extraction again.
          </span>
        </p>
      )}

      {notice && (
        <p className="mt-2 text-xs text-muted-foreground flex items-start gap-1">
          <Info className="h-3.5 w-3.5 mt-px shrink-0" />
          <span>{notice}</span>
        </p>
      )}

      {cells.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
          <Badge variant="outline" className="border-amber-500/50 text-amber-700 dark:text-amber-400">
            {pending.length} awaiting your review
          </Badge>
          {accepted > 0 && (
            <Badge variant="outline" className="border-emerald-500/50 text-emerald-700 dark:text-emerald-400">
              {accepted} approved
            </Badge>
          )}
        </div>
      )}

      {/* EXTRACT-UNITS-1 — asked before the period question, because a document
          held on units has no figures in the grid for a period question to be
          about. */}
      <ScaleNotice
        held={heldScale}
        resolved={resolvedScale}
        busy={busy}
        running={running}
        summarize={fields => summarizeFields(fields, currency)}
        onConfirm={docId => run({ confirm_scale: [docId] })}
        onDecline={docId => run({ decline_scale: [docId] })}
      />

      {held.length > 0 && (
        <div className="mt-2 rounded-lg border border-amber-500/60 bg-amber-500/5 p-2">
          <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 flex items-start gap-1">
            <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />
            <span>
              {held.length === 1
                ? "1 period needs your decision before it can go into the grid"
                : `${held.length} periods need your decision before they can go into the grid`}
            </span>
          </p>
          <ul className="mt-1.5 space-y-2">
            {held.map(h => (
              <li key={h.year} className="text-xs">
                <span className="text-brand-text">{h.reason}</span>
                <span className="mt-0.5 block text-muted-foreground">
                  Read from {h.filename || "an uploaded document"}
                  {h.period_label ? ` (${h.period_label})` : ""} —{" "}
                  {summarizeFields(h.fields, currency)}. Nothing from this period
                  has been added to the grid.
                </span>
                <span className="mt-1 flex flex-wrap gap-2">
                  <Button
                    size="sm" variant="outline" disabled={busy}
                    onClick={() => run({ confirm_years: [h.year] })}
                  >
                    {running && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                    FY{h.year} is correct — add it
                  </Button>
                  <Button
                    size="sm" variant="ghost" disabled={busy}
                    onClick={() => run({ discard_years: [h.year] })}
                  >
                    Discard FY{h.year}
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {discarded.length > 0 && (
        <details className="mt-2 rounded-lg border border-border/60 p-2 text-xs">
          <summary className="cursor-pointer font-medium text-brand-text">
            {discarded.length} period{discarded.length === 1 ? "" : "s"} you discarded
          </summary>
          <ul className="mt-1 space-y-1.5">
            {discarded.map(d => (
              <li key={`${d.year}-${d.filename}`}>
                <span className="text-muted-foreground">{d.reason}</span>
                {d.kept_fields && Object.keys(d.kept_fields).length > 0 && (
                  <span className="block text-muted-foreground">
                    Still in the grid for FY{d.year}:{" "}
                    {summarizeFields(d.kept_fields, currency)}.
                  </span>
                )}
                <Button
                  size="sm" variant="ghost" disabled={busy} className="mt-1"
                  onClick={() => run({ confirm_years: [d.year] })}
                >
                  Bring FY{d.year} back
                </Button>
              </li>
            ))}
          </ul>
        </details>
      )}

      {outOfWindow.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground flex items-start gap-1">
          <Info className="h-3.5 w-3.5 mt-px shrink-0" />
          <span>
            Figures were extracted for {outOfWindow.join(", ")}, which this
            workspace has not opened a column for yet. They are saved — reload
            the page to bring {outOfWindow.length === 1 ? "that period" : "those periods"} in.
          </span>
        </p>
      )}

      {mapCount > 0 && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setShowMap(v => !v)}
            className="text-xs font-medium text-brand-text underline underline-offset-2"
            aria-expanded={showMap}
          >
            {showMap ? "Hide" : "Show"} what was read from the documents
          </button>

          {showMap && (
            <div className="mt-1.5 space-y-2">
              {/* Read-only in v1: this is the audit trail from the document to
                  the grid. Reclassifying a line happens in the grid itself. */}
              <div className="overflow-x-auto rounded-lg border border-border/60">
                <table className="w-full min-w-[34rem] text-left text-[11px]">
                  <thead className="bg-muted/50 text-muted-foreground">
                    <tr>
                      <th className="px-2 py-1 font-medium">Line on the document</th>
                      <th className="px-2 py-1 font-medium">Went to</th>
                      <th className="px-2 py-1 font-medium text-right">Amount</th>
                      <th className="px-2 py-1 font-medium">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappedRows.map(({ key, year, cell }) => (
                      <tr key={key} className="border-t border-border/60 align-top">
                        <td className="px-2 py-1 text-brand-text">{cell.source_label}</td>
                        <td className="px-2 py-1">
                          FY{year} {fieldLabel(cell.field)}
                          {cell.sign_normalized && (
                            <span className="text-muted-foreground">
                              {" "}· sign normalized
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">
                          {fmt(cell.value, currency)}
                        </td>
                        <td className="px-2 py-1 text-muted-foreground">
                          {cell.filename}
                          <span className="block">{cell.locator}</span>
                        </td>
                      </tr>
                    ))}
                    {mappedRows.length === 0 && (
                      <tr className="border-t border-border/60">
                        <td className="px-2 py-1.5 text-muted-foreground" colSpan={4}>
                          No figures were carried into the grid from these documents.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {unmappedRows === undefined ? (
                <p className="text-[11px] text-muted-foreground">
                  This run was made before we recorded left-out lines. Run
                  extraction again to see every line the reader skipped.
                </p>
              ) : unmappedRows.length > 0 ? (
                <div className="overflow-x-auto rounded-lg border border-amber-500/40">
                  <table className="w-full min-w-[34rem] text-left text-[11px]">
                    <thead className="bg-amber-500/10 text-amber-700 dark:text-amber-400">
                      <tr>
                        <th className="px-2 py-1 font-medium" colSpan={3}>
                          {unmappedRows.length} line
                          {unmappedRows.length === 1 ? "" : "s"} read but not used —
                          check these before you rely on the totals
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {unmappedRows.map((u, i) => (
                        <tr key={i} className="border-t border-amber-500/30 align-top">
                          <td className="px-2 py-1 text-brand-text">{u.source_label}</td>
                          <td className="px-2 py-1 text-right tabular-nums">
                            {u.value == null ? "—" : fmt(u.value, currency)}
                          </td>
                          <td className="px-2 py-1 text-muted-foreground">
                            {u.reason}
                            <span className="block">{u.filename}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-[11px] text-muted-foreground">
                  Every line the reader recognized was carried into the grid.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {lastRun && (
        <div className="mt-2 space-y-1.5 text-xs">
          <p className="text-muted-foreground">
            Last run {lastRun.run_at ? new Date(lastRun.run_at).toLocaleString() : ""}
            {lastRun.run_by ? ` by ${lastRun.run_by}` : ""} —{" "}
            {lastRun.filled.length} filled, {lastRun.updated.length} refreshed,{" "}
            {lastRun.skipped_cpa_edited.length} left alone.
          </p>

          {lastRun.skipped_cpa_edited.length > 0 && (
            <details className="rounded-lg border border-border/60 p-2">
              <summary className="cursor-pointer font-medium text-brand-text">
                Kept your figures in {lastRun.skipped_cpa_edited.length} cell
                {lastRun.skipped_cpa_edited.length === 1 ? "" : "s"}
              </summary>
              <ul className="mt-1 space-y-0.5 text-muted-foreground">
                {lastRun.skipped_cpa_edited.map((s, i) => (
                  <li key={i}>
                    FY{s.year} {fieldLabel(s.field)}: kept {fmt(s.kept, currency)} —
                    the document said {fmt(s.extracted, currency)} ({s.reason})
                  </li>
                ))}
              </ul>
            </details>
          )}

          {lastRun.unassigned_period.length > 0 && (
            <details className="rounded-lg border border-amber-500/40 p-2">
              <summary className="cursor-pointer font-medium text-amber-700 dark:text-amber-400">
                {lastRun.unassigned_period.length} document
                {lastRun.unassigned_period.length === 1 ? "" : "s"} had no readable period
              </summary>
              <ul className="mt-1 space-y-0.5 text-muted-foreground">
                {lastRun.unassigned_period.map((u, i) => (
                  <li key={i}>
                    {u.filename} ({u.period_label || "no period stated"}) — figures
                    found but not filed to a year. Enter them manually.
                  </li>
                ))}
              </ul>
            </details>
          )}

          {lastRun.supporting.length > 0 && (
            <details className="rounded-lg border border-border/60 p-2">
              <summary className="cursor-pointer font-medium text-brand-text">
                {lastRun.supporting.length} cash document
                {lastRun.supporting.length === 1 ? "" : "s"} read for context
              </summary>
              <ul className="mt-1 space-y-0.5 text-muted-foreground">
                {lastRun.supporting.map((s, i) => (
                  <li key={i}>{s.filename} — {s.type.replace(/_/g, " ")}; cash
                    movement does not populate the P&amp;L.</li>
                ))}
              </ul>
            </details>
          )}

          {(lastRun.warnings.length > 0 || lastRun.errors.length > 0) && (
            <details className="rounded-lg border border-destructive/40 p-2" open>
              <summary className="cursor-pointer font-medium text-destructive">
                {lastRun.errors.length + lastRun.warnings.length} thing
                {lastRun.errors.length + lastRun.warnings.length === 1 ? "" : "s"} to check
              </summary>
              <ul className="mt-1 space-y-0.5 text-muted-foreground">
                {lastRun.errors.map((e, i) => (
                  <li key={`e${i}`}>{checkSentence(e)}</li>
                ))}
                {lastRun.warnings.map((w, i) => (
                  <li key={`w${i}`}>{checkSentence(w)}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

/** Compact provenance marker rendered beside a pre-filled input. */
export const ProvenanceBadge: React.FC<{
  cell: ProvenanceCell; edited: boolean; currency?: string;
}> = ({
  cell, edited, currency = DEFAULT_CURRENCY,
}) => {
  if (edited) {
    return (
      <span
        className="text-[10px] text-muted-foreground whitespace-nowrap"
        title={`Pre-filled as ${fmt(cell.value, currency)} from ${cell.filename}; you changed it.`}
      >
        edited
      </span>
    );
  }

  const detail = [
    `${cell.filename} — "${cell.source_label}" (${cell.locator})`,
    `parser: ${cell.parser}`,
    `confidence: ${Math.round(cell.confidence * 100)}%`,
    cell.sign_normalized ? "sign normalized to a positive expense" : "",
    cell.year_verified === false ? "fiscal year inferred — confirm it" : "",
    cell.note || "",
    cell.accepted_by ? `approved by ${cell.accepted_by}` : "",
  ].filter(Boolean).join("\n");

  const accepted = cell.status === "accepted";
  const uncertain = cell.confidence < 0.8 || cell.year_verified === false;

  return (
    <span
      title={detail}
      className={
        "text-[10px] px-1 py-px rounded whitespace-nowrap border cursor-help " +
        (accepted
          ? "border-emerald-500/50 text-emerald-700 dark:text-emerald-400"
          : "border-amber-500/50 text-amber-700 dark:text-amber-400")
      }
    >
      {accepted ? "approved" : uncertain ? "draft ?" : "draft"}
    </span>
  );
};
